<script setup lang="ts">
import { ref, watch, computed } from "vue"
import { Button } from "@/components/ui/button"
import { Filter } from "lucide-vue-next"
import { ZoomIn } from "lucide-vue-next"
import * as d3 from 'd3';
import {formatDateDDMMYYYY, dayMs, getWidthPerDay, scrollToToday} from "./planner.js";
import Callout from './Callout.vue'
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import DateInput from "./DateInput.vue"

import {useViewStore} from "@/stores/viewStore"

const view = useViewStore();

// ... your existing x scale and watch logic ...

// 1. Define cornerHeight as a computed property

const emit = defineEmits(['setProjectFilter', 'zoom']) 

const cornerEl = ref(null)

const active = ref(false)
const activeZoom = ref(false)
const activeZoomVal = ref(view.zoomVal || 0)

const props = defineProps({
  projectFilter: {type: Object, required: true},
  configs: { type: Array, required: true },
  printProject: {type: [Object, null], default: null},
});


const startDateStr = props.configs.find(c => c.name === "startdate").value;
const endDateStr = props.configs.find(c => c.name === "enddate").value;
const startDate =  new Date(startDateStr + "T00:00:00");
const endDate = new Date(endDateStr + "T00:00:00");
const initialStartDate = formatDateDDMMYYYY(startDate);
const initialEndDate = formatDateDDMMYYYY(endDate);

const entityStartDate = ref(initialStartDate);
const entityEndDate = ref(initialEndDate);


const totalDays = (endDate - startDate) / dayMs;
const x = d3.scaleUtc().domain([startDate, endDate]).range([0, totalDays * getWidthPerDay(props.printProject !== null, view.zoomWidthPerDay)]);

watch(
  () => props.projectFilter.all,
  (newVal) => {
	emit("setProjectFilter", newVal, props.projectFilter.active, props.projectFilter.planned)
  }
);

watch(
  () => props.projectFilter.active,
  (newVal) => {
	emit("setProjectFilter", props.projectFilter.all, newVal, props.projectFilter.planned)
  }
);

watch(
  () => props.projectFilter.planned,
  (newVal) => {
	emit("setProjectFilter", props.projectFilter.all, props.projectFilter.active, newVal)
  }
);

watch(activeZoomVal, (newValue, oldValue) => {
  console.log("changed from", oldValue, "to", newValue)

  if (newValue === 5) {
	//defer
	return
  }
  if (newValue === 0) {
	entityStartDate.value = initialStartDate;
	entityEndDate.value = initialEndDate;
  }
  emit('zoom', newValue, entityStartDate.value, entityEndDate.value);
})

function toggle() {
  console.log("toggle")
  active.value = !active.value
}

function toggleZoom() {
  console.log("toggle")
  activeZoom.value = !activeZoom.value
}

const cornerHeight = computed(() => {
  const baseHeight = props.printProject ? 160 : 96;
  return `${baseHeight}px`;
});

function handleStartDateChange(d) {
  console.log("handleStartDateChange=" + d);
  entityStartDate.value = d;
}

function handleEndDateChange(d) {
  console.log("handleEndDateChange=" + d);
  entityEndDate.value = d;
}

function handleZoomRange() {
  console.log("handleEndDateChange from=" + entityStartDate.value + " to=" + entityEndDate.value);
  emit('zoom', 4, entityStartDate.value, entityEndDate.value);
}
</script>

<template>

  <div v-if="props.printProject" ref="cornerEl" :style="{height: cornerHeight}" class="printview corner">
    <div style="display: block; width: 60px; height: 60px;">
       <img src="@/components/images/timin.svg" alt="Timin Logo" style="display: block; width: 48px; height: auto;" />
    </div>
	<div class="teamstrength">
	Team
	</div>
  </div>
  
  <div v-if="!props.printProject" ref="cornerEl" :style="{height: cornerHeight}" class="corner flex flex-col space-y-2">
    <button
      id="today-button"
      @click="scrollToToday(x, view)"
      class="px-3 py-2 rounded bg-blue-500 text-white hover:bg-blue-600"
    >
      Today
    </button>

	
	<div class="flex flex-row space-x-2">
      <Button class="p-2 text-black hover:text-gray-500"
             :class="active ? 'bg-slate-100' : 'bg-white'"
             aria-label="Filter"
             @click.stop="toggle">
        <Filter class="w-5 h-5" />
      </Button>

      <Button class="p-2 text-black hover:text-gray-500"
              :class="activeZoom ? 'bg-slate-100' : 'bg-white'"
              aria-label="Zoom"
              @click.stop="toggleZoom">
        <ZoomIn class="w-5 h-5" />
      </Button>
    </div>

   <Callout id="projectFilterPopup" :target="cornerEl" :show="active" @close="active = false">
	  <div class="flex flex-col space-y-2">
	  Projekte filtern
	  <div class="spacer" />
	  <div class="flex flex-col space-y-4 w-64">
		<RadioGroup v-model="props.projectFilter.all" class="flex flex-col space-y-3">
		  <div class="flex items-center space-x-2">
		    <RadioGroupItem id="option1" :value="false" />
		    <Label for="option1">Meine Buchungen</Label>
		  </div>
		  <div class="flex items-center space-x-2">
		    <RadioGroupItem id="option2" :value="true" />
		    <Label for="option2">Alle</Label>
		  </div>
		</RadioGroup>
	  </div>

      <div class="spacer" />
	  
	  <div class="flex flex-col space-y-4 w-64">
		  <div  class="flex items-center space-x-2">
	        <Checkbox
	          id="option1"
	          v-model="props.projectFilter.active"
	        />
	        <Label for="option1">Aktive Projekte</Label>
	      </div>

	      <div class="flex items-center space-x-2">
	        <Checkbox
	          id="option2"
	          v-model="props.projectFilter.planned"
	        />
	        <Label for="option2">Potentielle Projekte</Label>
	      </div>
		  
      </div>
	</div>
   </Callout>

	<Callout
	id="projectZoomPopup"
	:target="cornerEl"
	:show="activeZoom"
	@close="active = false"
	>
	<div class="flex flex-col space-y-2">
		Zoomstufen

		<div class="spacer" />

		<div class="flex flex-col space-y-4 w-64">
		<RadioGroup v-model="activeZoomVal" class="flex flex-col space-y-3">

			<div
			class="flex items-center space-x-2 cursor-pointer"
			@click="activeZoomVal = 0"
			>
			<RadioGroupItem id="zoom-all" :value="0" />
			<Label for="zoom-all">Standard</Label>
			</div>

			<div
			class="flex items-center space-x-2 cursor-pointer"
			@click="activeZoomVal = 1"
			>
			<RadioGroupItem id="zoom-week" :value="1" />
			<Label for="zoom-week">Woche</Label>
			</div>

			<div
			class="flex items-center space-x-2 cursor-pointer"
			@click="activeZoomVal = 2"
			>
			<RadioGroupItem id="zoom-month" :value="2" />
			<Label for="zoom-month">Monat</Label>
			</div>

			<div
			class="flex items-center space-x-2 cursor-pointer"
			@click="activeZoomVal = 3"
			>
			<RadioGroupItem id="zoom-quarter" :value="3" />
			<Label for="zoom-quarter">Quartal</Label>
			</div>

			<div
			class="flex items-center space-x-2 cursor-pointer"
			@click="activeZoomVal = 4"
			>
			<RadioGroupItem id="zoom-quarter" :value="4" />
			<Label for="zoom-quarter">Halbjahr</Label>
			</div>

			<div
			class="flex items-center space-x-2 cursor-pointer"
			@click="activeZoomVal = 5"
			>
			<RadioGroupItem id="zoom-custom" :value="5" />
			<Label for="zoom-custom">Individuell</Label>
			</div>

		</RadioGroup>

		<!-- Special case area -->
		<div
			v-show="activeZoomVal === 5"
			class="
			ml-6
			pl-4
			pr-3
			py-3
			border
			rounded-md
			bg-muted/30
			space-y-3
			"
		>
			<DateInput
			label="Start"
			:modelValue="entityStartDate"
			@datechange="handleStartDateChange"
			/>

			<DateInput
			label="End"
			:modelValue="entityEndDate"
			@datechange="handleEndDateChange"
			/>

			<div class="flex justify-end pt-1">
			<Button @click.stop="handleZoomRange">
				Bestätigen
			</Button>
			</div>
		</div>

		</div>
	</div>
	</Callout>
  </div>
</template>

<style>

.printview.corner {
  display: block; /* Change from flex if possible */
  padding-left: 10px;
  padding-top: 10px;
  width: 150px; /* Force a width wider than the sidebarWidthMm if needed */
  background-color: white !important;
}

.teamstrength {
  display: flex;
  font-size: 12px;
  height: 85px;
  align-items: end;
  width: 100%;
}

</style>